/* eslint-disable @typescript-eslint/no-explicit-any */

import Highcharts from 'highcharts'
import HC_heatmap from 'highcharts/modules/heatmap'
import { useEffect, useId, useState } from 'react'
import { AttacksHeatmapMapperData } from '../../../types/StatTypes'
import ChartSkeleton from '../../skeleton/ChartSkeleton'
import { useSelector } from 'react-redux'
import { selectUser } from '../../../reducers/users'
import BaseChart from '../../charts/BaseChart'

interface IVisualizationsBasicHeatmapProps {
  chartData: AttacksHeatmapMapperData
  height?: string
  logarithmic?: boolean
}

const VisualizationsBasicHeatmap = (props: IVisualizationsBasicHeatmapProps) => {
  const { chartData, height, logarithmic } = props
  const [isLoading, setIsLoading] = useState(true)
  const { darkModeEnabled } = useSelector(selectUser)
  const textColor = darkModeEnabled ? '#FFFFFF' : '#1c2024'
  const chartId = useId()

  useEffect(() => {
    if (typeof HC_heatmap === 'function') {
      HC_heatmap(Highcharts)
    }
  }, [])

  useEffect(() => {
    if (chartData) {
      setIsLoading(true)
      const timer = setTimeout(() => setIsLoading(false), 600)
      return () => clearTimeout(timer)
    }
  }, [chartData])

  const heatmapOptions: any = {
    chart: {
      type: 'heatmap',
      plotBorderWidth: 1,
      backgroundColor: 'transparent',
      height: `${height ? height : '512px'}`,
    },
    title: { text: null },
    xAxis: {
      categories: chartData.xAxis,
      labels: {
        style: {
          color: textColor,
        },
      },
    },
    yAxis: {
      categories: chartData.yAxis,
      title: null,
      reversed: false,
      labels: {
        style: {
          color: textColor,
        },
      },
    },
    series: [
      {
        name: 'Events per Month',
        borderWidth: 1,
        borderColor: '#ffffff',
        data: chartData.heatmapData,
        dataLabels: { enabled: false },
      },
    ],
    colorAxis: {
      min: 0,
      minColor: '#ffc9c9',
      maxColor: '#c92a2a',
    },
    tooltip: {
      formatter: function (this: Highcharts.Point): string {
        const point = this as any
        const eventLabel = point.point.value === 1 ? 'event' : 'events'
        const xCategory = this.series.xAxis.categories[point.point.x] ?? 'Unknown'
        const yCategory = this.series.yAxis.categories[point.point.y] ?? 'Unknown'
        const value = point.point.value ?? 0

        return `<b>${value}</b> ${eventLabel}<br/>in <b>${xCategory}</b> for <b>${yCategory}</b>`
      },
    },

    legend: { enabled: false },
    credits: { enabled: false },
    plotOptions: {
      heatmap: {
        turboThreshold: 20000,
      },
    },
  }

  if (logarithmic) {
    heatmapOptions.colorAxis = {
      min: 1,
      type: 'logarithmic',
      minColor: '#ffc9c9',
      maxColor: '#c92a2a',
      stops: [
        [0, '#ffc9c9'],
        [0.6, '#fa5252'],
        [1, '#c92a2a'],
      ],
    }
  }

  return <>{isLoading ? <ChartSkeleton fullWidth={true} /> : <BaseChart options={heatmapOptions} id={chartId} />}</>
}

export default VisualizationsBasicHeatmap
